/**
 * Beyond Sure — Features catalog (canonical, ported from form.html:9043-9410).
 *
 * The single source of truth for both:
 *   - the /features catalog page (search + filter + grid)
 *   - the /features/:slug detail page (header + body)
 *
 * Every name + category here matches the founder's live form.html sidebar
 * exactly, so the new React shell carries the same substance the customer
 * is already using in production. The mockBody is a calm one-paragraph
 * placeholder until the per-feature surface is wired to /api.
 *
 * Counts (must total 62, mirrors form.html):
 *   - My Insurance      3
 *   - Discover          3
 *   - Claims           18
 *   - Smart Tools       8
 *   - New Features      7
 *   - Advanced Tools   23
 *   - Specialized       0  (no items in form.html; group auto-hides)
 *
 * // TODO: replace each mockBody with the real per-feature surface wired
 *   to /api/* endpoints — one PR per feature is fine; this file stays
 *   the source of truth for routing + the catalog page.
 */

export const FEATURE_CATEGORIES = [
  "My Insurance",
  "Discover",
  "Claims",
  "Smart Tools",
  "New Features",
  "Advanced Tools",
  "Specialized",
] as const;

export type FeatureCategory = (typeof FEATURE_CATEGORIES)[number];

export type FeatureTier = "lite" | "pro" | "enterprise";

/**
 * Icon name → rendered by the local IconBadge component using a small
 * SVG set. Keeps the catalog pure-data (no JSX imports) so it can
 * be filtered/sorted/searched cheaply.
 */
export type FeatureIcon =
  | "shield"
  | "compare"
  | "audit"
  | "wallet"
  | "doc"
  | "claim"
  | "renewal"
  | "calculator"
  | "spark"
  | "graph"
  | "search"
  | "team"
  | "calendar"
  | "phone"
  | "globe"
  | "lock"
  | "bell"
  | "lightning"
  | "star"
  | "file";

export interface FeatureDef {
  /** Stable URL slug — used for /features/:slug routing. */
  slug: string;
  name: string;
  /** One-line description (≤ ~80 chars). */
  hint: string;
  category: FeatureCategory;
  icon: FeatureIcon;
  /** Optional tier badge — "lite" + free features omit this. */
  tier?: FeatureTier;
  /** Optional "NEW" pill for first-30-day surfacing. */
  isNew?: boolean;
  /** Mock body markdown shown on the detail template until wired. */
  mockBody: string;
}

export const FEATURES: FeatureDef[] = [
  // ─── My Insurance (3) ─────────────────────────────────────────
  {
    slug: "plain-summary",
    name: "Plain Summary",
    hint: "Turn any policy PDF into a plain-language summary.",
    category: "My Insurance",
    icon: "doc",
    mockBody:
      "Upload a policy and I'll give you the one-page version: " +
      "what's covered, what isn't, when it pays out, what to watch. " +
      "No insurance jargon, no fine-print bait.",
  },
  {
    slug: "ask-my-policy",
    name: "Ask My Policy",
    hint: "Ask any question about your policy in plain words.",
    category: "My Insurance",
    icon: "search",
    mockBody:
      "Have your policy in hand. Ask me anything — \"is my dad's knee " +
      "surgery covered?\", \"what's the room-rent cap?\". I quote the " +
      "exact clause back at you, page + line.",
  },
  {
    slug: "truth-audit",
    name: "Truth Audit",
    hint: "Fact-check what your agent told you, line-by-line.",
    category: "My Insurance",
    icon: "audit",
    mockBody:
      "Paste what your agent said (text or screenshot). I check every " +
      "claim against the actual policy and IRDAI rules. Anything that " +
      "doesn't hold up gets flagged in red.",
  },

  // ─── Discover (3) ─────────────────────────────────────────────
  {
    slug: "browse-all-plans",
    name: "Browse All Plans",
    hint: "Browse every plan across every insurer in India.",
    category: "Discover",
    icon: "spark",
    mockBody:
      "Health, life, motor, travel, home, accident — every IRDAI-licensed " +
      "insurer, every plan, filterable by what actually matters to you. " +
      "No sponsored rankings, no commission bait.",
  },
  {
    slug: "add-by-scenario",
    name: "Add by Scenario",
    hint: "Tell me your situation — I'll match you to a plan.",
    category: "Discover",
    icon: "doc",
    mockBody:
      "Describe your life: \"32, married, one kid, parents in their 60s\". " +
      "I shortlist 3 plans across categories, each with the exact reason " +
      "it fits your shape.",
  },
  {
    slug: "compare-plans",
    name: "Compare Plans",
    hint: "Side-by-side comparison across insurers.",
    category: "Discover",
    icon: "compare",
    mockBody:
      "Pick 2-6 plans, see them in a single table: premium, sum insured, " +
      "claim ratio, sub-limits, exclusions, network. Differences get " +
      "highlighted so you can decide in two minutes, not two weeks.",
  },

  // ─── Claims (18) ──────────────────────────────────────────────
  {
    slug: "emergency-sos",
    name: "Emergency SOS",
    hint: "In an emergency? Tap once. I do the rest.",
    category: "Claims",
    icon: "phone",
    mockBody:
      "Hospitalization right now? Tap SOS. I pull your active health " +
      "policies, find the nearest cashless hospital in network, prep " +
      "the pre-auth request, and call your insurer's claims desk.",
  },
  {
    slug: "rejection-decoder",
    name: "Rejection Decoder",
    hint: "Decode why a claim was rejected — in 60 seconds.",
    category: "Claims",
    icon: "audit",
    mockBody:
      "Paste the rejection letter. I translate the legalese into the real " +
      "reason, tell you whether the rejection is fair, and draft your " +
      "next move (appeal, ombudsman, or accept).",
  },
  {
    slug: "ncb-claim-calc",
    name: "NCB Claim Calc",
    hint: "Will claiming hurt your No-Claim Bonus? See the math.",
    category: "Claims",
    icon: "calculator",
    mockBody:
      "Tell me the claim amount + your current NCB%. I show you the " +
      "5-year cost of claiming vs. paying out of pocket, including next " +
      "year's premium hike. Sometimes it's cheaper not to claim.",
  },
  {
    slug: "incident-logger",
    name: "Incident Logger",
    hint: "Log the incident before you forget the details.",
    category: "Claims",
    icon: "file",
    mockBody:
      "Accident, hospitalization, theft, damage — log it the same day " +
      "while it's fresh. Time, place, witnesses, photos, what happened. " +
      "Turns into your claim packet later.",
  },
  {
    slug: "file-claim",
    name: "File a Claim",
    hint: "I draft the claim paperwork. You sign.",
    category: "Claims",
    icon: "claim",
    mockBody:
      "Walks you through the whole filing: required documents, claim " +
      "form fields, hospital coordination, deadline tracker. You only " +
      "have to physically sign + send what I can't.",
  },
  {
    slug: "my-claims",
    name: "My Claims",
    hint: "Every claim you've ever filed, in one timeline.",
    category: "Claims",
    icon: "doc",
    mockBody:
      "All your past + active claims with status, insurer SLA clock, " +
      "documents submitted, and what's pending. One place to see " +
      "everything that's owed to you.",
  },
  {
    slug: "appeal-generator",
    name: "Appeal Generator",
    hint: "Generate a formal appeal letter for a rejected claim.",
    category: "Claims",
    icon: "doc",
    mockBody:
      "Tell me what the insurer said + why you disagree. I generate a " +
      "letter that cites the exact policy clauses + IRDAI rules + " +
      "precedent ombudsman cases that support you.",
  },
  {
    slug: "worst-case-playbooks",
    name: "Worst-Case Playbooks",
    hint: "What to do in the worst week of your life — step by step.",
    category: "Claims",
    icon: "doc",
    mockBody:
      "Death in the family, major hospitalization, total car loss, house " +
      "fire — each playbook is a calm checklist for the next 72 hours " +
      "while you're not thinking straight.",
  },
  {
    slug: "letter-templates",
    name: "Letter Templates",
    hint: "Ready-made letters: complaints, escalations, notices.",
    category: "Claims",
    icon: "doc",
    mockBody:
      "100+ templates that have actually worked: complaint to the " +
      "grievance cell, escalation to IRDAI, legal notice, RTI to " +
      "insurer. Fill the blanks, send.",
  },
  {
    slug: "claim-predictor",
    name: "Claim Predictor",
    hint: "Will this claim be approved? My estimate before you file.",
    category: "Claims",
    icon: "spark",
    mockBody:
      "Describe the situation + policy. I give you my probability the " +
      "claim gets approved as filed, the top 3 reasons it might get " +
      "rejected, and what to fix before sending.",
  },
  {
    slug: "precedent-search",
    name: "Precedent Search",
    hint: "Find ombudsman + court rulings similar to your case.",
    category: "Claims",
    icon: "search",
    mockBody:
      "Search 10,000+ ombudsman + consumer-court rulings. Find cases " +
      "that look like yours and see what the ruling was — useful " +
      "ammunition for your appeal.",
  },
  {
    slug: "legal-navigator",
    name: "Legal Navigator",
    hint: "Know your IRDAI rights and what to escalate, when.",
    category: "Claims",
    icon: "doc",
    mockBody:
      "Every step of the legal escalation ladder: grievance cell → " +
      "ombudsman → consumer court → high court. What each costs, what " +
      "each takes, what's worth doing.",
  },
  {
    slug: "hospital-network",
    name: "Hospital Network",
    hint: "Which hospitals are cashless under your policy?",
    category: "Claims",
    icon: "globe",
    mockBody:
      "Live network list pulled per policy, searchable by city + " +
      "speciality. Includes ratings + recent claim-settlement notes " +
      "from other Beyond Sure users.",
  },
  {
    slug: "sla-timers",
    name: "SLA Timers",
    hint: "How long can each step take? Insurer-by-insurer SLAs.",
    category: "Claims",
    icon: "bell",
    mockBody:
      "Every IRDAI-mandated timeline + every insurer's published SLA, " +
      "for every step of every claim type. When the clock is past due, " +
      "you have grounds to escalate.",
  },
  {
    slug: "premium-tracker",
    name: "Premium Tracker",
    hint: "Every premium you've ever paid, indexed and searchable.",
    category: "Claims",
    icon: "wallet",
    mockBody:
      "Full premium history across every policy + every year. Useful " +
      "for tax filing, claim audit, and noticing when an insurer " +
      "quietly raises your premium more than the rest.",
  },
  {
    slug: "lapse-recovery",
    name: "Lapse Recovery",
    hint: "Lapsed by accident? Here's how to reinstate.",
    category: "Claims",
    icon: "renewal",
    mockBody:
      "Step-by-step reinstatement: grace-period rules, reinstatement " +
      "fee, medical re-underwriting risk, and whether it's cheaper to " +
      "buy a fresh policy.",
  },
  {
    slug: "post-claim-analysis",
    name: "Post-Claim Analysis",
    hint: "What that claim taught us about your coverage gaps.",
    category: "Claims",
    icon: "audit",
    mockBody:
      "After every claim — approved or rejected — a calm review of what " +
      "your policy actually covered vs. what you needed. So next year's " +
      "policy doesn't have the same hole.",
  },
  {
    slug: "activation-checklist",
    name: "Activation Checklist",
    hint: "Just bought a policy? Check these 8 things first.",
    category: "Claims",
    icon: "doc",
    mockBody:
      "Day-1 checklist for a fresh policy: nominee on record, KYC " +
      "complete, medical disclosures matched, network confirmed, " +
      "premium auto-debit set up. Most claims fail because of these.",
  },

  // ─── Smart Tools (8) ──────────────────────────────────────────
  {
    slug: "cover-calculator",
    name: "Cover Calculator",
    hint: "How much cover do you actually need? Real math, not rules of thumb.",
    category: "Smart Tools",
    icon: "calculator",
    mockBody:
      "Tell me your income, dependents, debts, lifestyle, location. I " +
      "compute term life + health + accident cover sized to *your* " +
      "shape, not a flat \"10x salary\" cliché.",
  },
  {
    slug: "senior-mode",
    name: "Senior Mode",
    hint: "Parent-friendly mode: bigger text, simpler words.",
    category: "Smart Tools",
    icon: "team",
    mockBody:
      "Toggle to senior mode: 18pt+ text, no jargon, slower walk-through, " +
      "voice-out option. Designed for parents who'd rather you handled it " +
      "but want to understand what's happening.",
  },
  {
    slug: "premium-guardian",
    name: "Premium Guardian",
    hint: "Catch premium hikes the moment they renew.",
    category: "Smart Tools",
    icon: "shield",
    mockBody:
      "30 days before each renewal, I check what the insurer is quoting " +
      "vs. comparable plans. If they hiked you more than the market, I " +
      "tell you + give you 3 portability candidates.",
  },
  {
    slug: "tax-autopilot",
    name: "Tax Autopilot",
    hint: "Auto-fill your insurance tax declarations.",
    category: "Smart Tools",
    icon: "doc",
    mockBody:
      "Pre-fills 80C, 80D, 80CCC declarations from your policy data + " +
      "premium history. One-tap export to your CA or to ITR-1/ITR-2.",
  },
  {
    slug: "spam-shield",
    name: "Spam Shield",
    hint: "Block insurance spam calls + WhatsApp forwards.",
    category: "Smart Tools",
    icon: "shield",
    mockBody:
      "Forward the suspicious call/text/forward. I tell you who they " +
      "really are, whether it's a scam, and how to block them — " +
      "TRAI complaint pre-filled if it's a violation.",
  },
  {
    slug: "family-legacy-kit",
    name: "Family Legacy Kit",
    hint: "What your family needs to know if something happens to you.",
    category: "Smart Tools",
    icon: "team",
    mockBody:
      "Generates a single calm PDF: every policy you own, every nominee, " +
      "every claim phone number, every locker key. Locked with a code " +
      "your spouse + child know — opened only when needed.",
  },
  {
    slug: "portability-check",
    name: "Portability Check",
    hint: "Switching insurers? See if your continuity-of-cover transfers.",
    category: "Smart Tools",
    icon: "renewal",
    mockBody:
      "Most people lose waiting-period credit when they switch. I check " +
      "whether your new policy honors continuity, what paperwork the " +
      "new insurer needs, and the 45-day porting window math.",
  },
  {
    slug: "employer-gap",
    name: "Employer Gap",
    hint: "If you leave your job tomorrow, what coverage disappears?",
    category: "Smart Tools",
    icon: "shield",
    mockBody:
      "Map the group cover your employer gives you, then map what " +
      "actually transfers (almost nothing) if you switch jobs. Recommends " +
      "what to buy *now* while you're still healthy and under-30.",
  },

  // ─── New Features (7) ─────────────────────────────────────────
  {
    slug: "insurance-iq",
    name: "Insurance IQ",
    hint: "5-minute quiz: how insurance-savvy are you, really?",
    category: "New Features",
    icon: "star",
    isNew: true,
    mockBody:
      "20 short questions across health, life, motor, and claim handling. " +
      "Get a score + the exact 3 things you should learn next. Calibrated " +
      "against ~10k Beyond Sure users.",
  },
  {
    slug: "decision-journal",
    name: "Decision Journal",
    hint: "Record every insurance decision + why. Future-you will thank you.",
    category: "New Features",
    icon: "doc",
    isNew: true,
    mockBody:
      "Every time you buy, switch, claim, or skip — log it with the " +
      "reasoning. Year-later, you see which decisions held up + which " +
      "were biased. Better than memory.",
  },
  {
    slug: "scenario-simulator",
    name: "Scenario Simulator",
    hint: "What if you got hit by a truck tomorrow? See the payout.",
    category: "New Features",
    icon: "spark",
    isNew: true,
    mockBody:
      "Pick a scenario (death, disability, hospitalization, total car loss). " +
      "I compute the exact payout from each of your active policies, " +
      "the gap, and whether your family lands on their feet or not.",
  },
  {
    slug: "grievance-timer",
    name: "Grievance Timer",
    hint: "Filed a complaint? Watch the IRDAI SLA clock tick.",
    category: "New Features",
    icon: "bell",
    isNew: true,
    mockBody:
      "Live timer on every grievance you've filed with the insurer's " +
      "grievance cell or IRDAI's IGMS portal. When the SLA expires, " +
      "I prep your escalation letter automatically.",
  },
  {
    slug: "family-meeting",
    name: "Family Meeting",
    hint: "Run a 15-min family insurance huddle. Agenda included.",
    category: "New Features",
    icon: "team",
    isNew: true,
    mockBody:
      "Pre-built agenda: who's covered, who isn't, what each policy pays, " +
      "where the documents live, what to do at 2am. Designed for a single " +
      "Sunday-evening conversation with your spouse + adult children.",
  },
  {
    slug: "hundred-days-checklist",
    name: "100 Days Checklist",
    hint: "First 100 days with a new policy: what to do, in order.",
    category: "New Features",
    icon: "doc",
    isNew: true,
    mockBody:
      "Day 1: KYC + nominee. Day 7: medical disclosures matched. Day 30: " +
      "premium auto-debit confirmed. Day 90: first wellness benefit used. " +
      "Each ticked item is one less claim risk later.",
  },
  {
    slug: "policy-change-detector",
    name: "Policy Change Detector",
    hint: "Catch the moment your insurer quietly changes the terms.",
    category: "New Features",
    icon: "bell",
    isNew: true,
    mockBody:
      "I diff every renewal against last year's policy wording. Any " +
      "exclusion that got added, any sub-limit that tightened, any " +
      "room-rent cap that dropped — flagged before you accept renewal.",
  },

  // ─── Advanced Tools (23) ──────────────────────────────────────
  {
    slug: "premium-estimator",
    name: "Premium Estimator",
    hint: "Estimate the premium for any plan, before you ask the insurer.",
    category: "Advanced Tools",
    icon: "calculator",
    mockBody:
      "Plug in age, city, sum insured, plan. I estimate the premium for " +
      "the top 12 insurers with confidence bands — useful for budgeting + " +
      "for catching agents who quote inflated prices.",
  },
  {
    slug: "quick-diagnostic",
    name: "Quick Diagnostic",
    hint: "10-second check: are you over-insured, under-insured, or right?",
    category: "Advanced Tools",
    icon: "audit",
    mockBody:
      "Tell me your income + active premiums. I score your cover-to-income " +
      "ratio against benchmarks for your age + life-stage. Verdict in one " +
      "sentence + 3 next moves.",
  },
  {
    slug: "coverage-gap-map",
    name: "Coverage Gap Map",
    hint: "Map every gap in your cover, ranked by risk.",
    category: "Advanced Tools",
    icon: "graph",
    mockBody:
      "Visual map of every risk you're exposed to (death, hospitalization, " +
      "disability, liability, asset loss) and what % each is covered. " +
      "Red gaps first.",
  },
  {
    slug: "spend-reality-check",
    name: "Spend Reality Check",
    hint: "How much have you actually spent on insurance? You'll be surprised.",
    category: "Advanced Tools",
    icon: "wallet",
    mockBody:
      "Total lifetime spend across all policies, broken down by line " +
      "(life, health, motor, etc.) + by insurer. Often more than people " +
      "expect — useful for evaluating whether you've been over-served.",
  },
  {
    slug: "negotiation-coach",
    name: "Negotiation Coach",
    hint: "How to negotiate your premium down with your insurer.",
    category: "Advanced Tools",
    icon: "team",
    mockBody:
      "Scripts + leverage points: loyalty length, NCB level, comparable " +
      "quotes, your claim history. Insurers do give discounts when asked " +
      "the right way at the right time.",
  },
  {
    slug: "decision-time-capsule",
    name: "Decision Time Capsule",
    hint: "Lock in today's reasoning. Revisit it at renewal.",
    category: "Advanced Tools",
    icon: "calendar",
    mockBody:
      "Record why you picked this policy + what you'd switch to if X " +
      "changed. Auto-resurfaces 60 days before renewal so you don't " +
      "auto-renew out of inertia.",
  },
  {
    slug: "pay-for-itself-calc",
    name: "Pay for Itself Calc",
    hint: "When does this policy stop being an expense and start being an asset?",
    category: "Advanced Tools",
    icon: "wallet",
    mockBody:
      "For each policy, the break-even year: when cumulative premium = " +
      "expected payout × probability. Reframes \"why am I paying for " +
      "this\" into \"this paid for itself in year 7\".",
  },
  {
    slug: "what-would-a-pro-do",
    name: "What Would A Pro Do",
    hint: "A licensed advisor's exact playbook for your situation.",
    category: "Advanced Tools",
    icon: "star",
    mockBody:
      "Describe your situation. I walk you through what a fee-only " +
      "(non-commission) advisor would do, step by step, with no skin " +
      "in the game of selling you the next policy.",
  },
  {
    slug: "insurance-translator",
    name: "Insurance Translator",
    hint: "Translate insurance jargon into the words your mom uses.",
    category: "Advanced Tools",
    icon: "globe",
    mockBody:
      "Paste any policy clause, term, or letter. I rewrite it in the " +
      "language you actually speak — including Hinglish, Tanglish, " +
      "Bengali, Tamil, Marathi, Gujarati, and 8 more.",
  },
  {
    slug: "confidence-builder",
    name: "Confidence Builder",
    hint: "Practice the conversation you're about to have with your agent.",
    category: "Advanced Tools",
    icon: "shield",
    mockBody:
      "Mock conversation with a roleplay agent. I'll push you the way a " +
      "real commission-driven agent would, and coach you on the answers " +
      "+ pushback that hold the line.",
  },
  {
    slug: "hlv-calculator",
    name: "HLV Calculator",
    hint: "Human Life Value — how much is your earning power worth?",
    category: "Advanced Tools",
    icon: "calculator",
    mockBody:
      "HLV = (annual income - personal expenses) discounted to today " +
      "across your earning years. The honest answer to \"how much term " +
      "life cover should I actually buy\".",
  },
  {
    slug: "motor-idv-calc",
    name: "Motor IDV Calc",
    hint: "What's your car worth? IDV that holds up to a claim.",
    category: "Advanced Tools",
    icon: "calculator",
    mockBody:
      "Insured Declared Value by make, model, year, city, and " +
      "depreciation curve. Insurers love quoting low IDVs to cut " +
      "premiums; this gives you the real number to negotiate from.",
  },
  {
    slug: "coverage-forecast",
    name: "Coverage Forecast",
    hint: "Where will your cover be in 5 years? In 10?",
    category: "Advanced Tools",
    icon: "graph",
    mockBody:
      "Projects your future cover-to-income ratio assuming today's " +
      "policies + premium trajectory. Surfaces the moments you'll be " +
      "dangerously under-covered (e.g., 50s with growing parent risk).",
  },
  {
    slug: "tricky-questions",
    name: "Tricky Questions",
    hint: "The questions agents hope you don't ask. Asked.",
    category: "Advanced Tools",
    icon: "search",
    mockBody:
      "12 questions for any policy: room-rent cap, sub-limits, co-pay, " +
      "waiting periods, exclusion list, restoration, NCB structure, claim " +
      "ratio, network changes, premium hike pattern. Pre-filled per plan.",
  },
  {
    slug: "myth-debunker",
    name: "Myth Debunker",
    hint: "10 insurance myths Indians believe. Debunked with evidence.",
    category: "Advanced Tools",
    icon: "audit",
    mockBody:
      "\"LIC is safer.\" \"Term plans don't pay out.\" \"You need 10x salary.\" " +
      "Each myth gets the data + the IRDAI-verified reality. Useful as a " +
      "send-to-parents PDF.",
  },
  {
    slug: "am-i-overpaying",
    name: "Am I Overpaying?",
    hint: "Compare your premium to the cheapest equivalent plan. Be brave.",
    category: "Advanced Tools",
    icon: "wallet",
    mockBody:
      "Drops your current policy details against the lowest-priced " +
      "equivalent plan with the same cover + claim ratio + network. " +
      "Sometimes you're fine. Often you're paying 1.5x for nothing.",
  },
  {
    slug: "second-opinion",
    name: "Second Opinion",
    hint: "Get a second opinion on what your current agent recommends.",
    category: "Advanced Tools",
    icon: "doc",
    mockBody:
      "Paste the agent's recommendation + quote. I review it the way an " +
      "independent fee-only advisor would, flag commission-bias signals, " +
      "and propose 2 alternatives that are usually cheaper + safer.",
  },
  {
    slug: "smart-reminders",
    name: "Smart Reminders",
    hint: "Renewal, premium, document — gentle nudges, never spam.",
    category: "Advanced Tools",
    icon: "bell",
    mockBody:
      "60-, 30-, 15-, 3-day reminders before every renewal. KYC due-date " +
      "watch. Medical-checkup window. All on your preferred channel, " +
      "and you can turn off any class with one tap.",
  },
  {
    slug: "regulatory-updates",
    name: "Regulatory Updates",
    hint: "Plain-English IRDAI updates that affect your wallet.",
    category: "Advanced Tools",
    icon: "bell",
    mockBody:
      "Every IRDAI circular, GST change, claim-process update, ombudsman " +
      "ruling — translated into \"here's what it means for you, in one " +
      "paragraph\". Monthly, not daily.",
  },
  {
    slug: "peace-of-mind-score",
    name: "Peace-of-Mind Score",
    hint: "One number: how confident should you be in your cover?",
    category: "Advanced Tools",
    icon: "star",
    mockBody:
      "Aggregates cover adequacy + insurer health + claim ratio + your " +
      "own preparedness (nominees, documents, family awareness) into one " +
      "0-100 score with the 3 biggest levers to move it up.",
  },
  {
    slug: "insurer-health-index",
    name: "Insurer Health Index",
    hint: "Which insurers are financially healthy? Quarterly ranking.",
    category: "Advanced Tools",
    icon: "graph",
    mockBody:
      "Solvency ratio, claim settlement ratio, claim repudiation ratio, " +
      "grievance ratio, regulator action — combined into a single ranking " +
      "refreshed every quarter from IRDAI's public filings.",
  },
  {
    slug: "community-qa",
    name: "Community Q&A",
    hint: "What other policyholders learned the hard way.",
    category: "Advanced Tools",
    icon: "team",
    mockBody:
      "Anonymized Q&A from other Beyond Sure users — \"my Star Health " +
      "claim got rejected for X, here's what worked\". Searchable by " +
      "insurer, plan, claim type.",
  },
  {
    slug: "rate-an-agent",
    name: "Rate an Agent",
    hint: "Honest reviews of agents in your city.",
    category: "Advanced Tools",
    icon: "star",
    mockBody:
      "Verified-customer reviews of insurance agents + agencies, scored " +
      "on honesty, claim help, response speed, and post-sale support. " +
      "City + speciality filters.",
  },
];

/**
 * Quick lookup helper used by the detail route.
 * O(n) is fine — the catalog is small + cached by Vite.
 */
export function findFeature(slug: string): FeatureDef | undefined {
  return FEATURES.find((f) => f.slug === slug);
}

/**
 * Group features by category in the canonical order.
 * Empty groups are omitted — so "Specialized" auto-hides
 * (form.html doesn't ship a Specialized group).
 */
export function groupByCategory(
  features: FeatureDef[],
): { category: FeatureCategory; items: FeatureDef[] }[] {
  return FEATURE_CATEGORIES
    .map((category) => ({
      category,
      items: features.filter((f) => f.category === category),
    }))
    .filter((g) => g.items.length > 0);
}
